import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import styled from 'styled-components';

import { FULL_DATE_FORMAT_WITH_SECONDS, now } from '../../utils/dates';

import './styles.css';

interface IEventCountdownProps {
  eventDate: string;
  showSeconds?: boolean;
  format?: string;
  classNames?: string;
  isMobile?: boolean;
}

interface ITimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const initialTimeRemaining: ITimeRemaining = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

interface ITimePartProps {
  hasTimeValue?: boolean;
  children?: React.ReactNode;
}

const EventCountdown: React.FC<IEventCountdownProps> = (props) => {
  const [timeRemaining, setTimeRemaining] = useState<ITimeRemaining>(initialTimeRemaining);
  const { eventDate = '', showSeconds = false, format = FULL_DATE_FORMAT_WITH_SECONDS, classNames, isMobile } = props;

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const nowTime = now();
      const eventMoment = moment(eventDate, format);


      if (!eventMoment.isValid()) {
        return;
      }

      if (nowTime.isAfter(eventMoment)) {
        setTimeRemaining(initialTimeRemaining);
        return;
      }

      const duration = moment.duration(eventMoment.diff(nowTime));

      const days = Math.floor(duration.asDays());
      const hours = duration.hours();
      const minutes = duration.minutes();
      const seconds = duration.seconds();



      setTimeRemaining({ days, hours, minutes, seconds });
    };

    calculateTimeRemaining();
    const intervalId = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(intervalId);
  }, [eventDate, showSeconds]);

  const hasValue = (value: number) => {
    return value > 0;
  };

  const hasDay = hasValue(timeRemaining.days);
  const hasHour = hasValue(timeRemaining.hours);
  const hasMinutes = hasValue(timeRemaining.minutes);
  const hasSeconds = hasValue(timeRemaining.seconds);

  const mobileCountdown = () => {
    return (
      <div className={clsx('countdownRoot', { ['mobileCountdownRoot']: isMobile, ['isLessOneDay']: !hasDay }, classNames)}>
        <MobileTimeContainer><TimePart hasTimeValue={hasDay}>{timeRemaining.days}</TimePart><Unit>d</Unit></MobileTimeContainer>
        <Unit>:</Unit>
        <MobileTimeContainer><TimePart hasTimeValue={hasHour}>{timeRemaining.hours}</TimePart><Unit>hr</Unit></MobileTimeContainer>
        <Unit>:</Unit>
        <MobileTimeContainer><TimePart hasTimeValue={hasMinutes}>{timeRemaining.minutes}</TimePart><Unit>min</Unit></MobileTimeContainer>
      </div>
    );
  };

  const desktopCountdown = () => {
    return (
      <div className={clsx('countdownRoot', { ['isLessOneDay']: !hasDay }, classNames)}>
        <div >
          <TimePart hasTimeValue={hasDay}>{timeRemaining.days}</TimePart><Unit> d : </Unit>
          <TimePart hasTimeValue={hasHour}>{timeRemaining.hours}</TimePart><Unit> hr : </Unit>
          <TimePart hasTimeValue={hasMinutes}>{timeRemaining.minutes}</TimePart><Unit> min {showSeconds && <Unit>:</Unit>}</Unit>
          {showSeconds && (
            <>
              <TimePart hasTimeValue={hasSeconds}> {timeRemaining.seconds}</TimePart><Unit> s</Unit>
            </>
          )}
        </div>
      </div >
    );
  };

  return (
    <>
      {isMobile ? mobileCountdown() : desktopCountdown()}
    </>
  );
};

export default EventCountdown;

const TimePart = styled.span<ITimePartProps>`
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 700;
  line-height: 18px;
  color: ${({ hasTimeValue }) => (hasTimeValue ? '#FFFFFF' : '#696969')};
`;

const Unit = styled.span`
  font-family: Nunito Sans;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: #696969;
  align-self: center;
`;

const MobileTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;